import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { authenticatedJsonRequest } from "services/api";
import { WHOLSTER_REST_BASE_URL } from "services/config";

async function loadLimit(url, sessionId) {
  const data = await authenticatedJsonRequest(url, sessionId);
  if (
    !Number.isSafeInteger(data?.dailyLimit) ||
    data.dailyLimit < 0 ||
    !Number.isSafeInteger(data.usedToday) ||
    data.usedToday < 0 ||
    !Number.isSafeInteger(data.remaining) ||
    typeof data.exceeded !== "boolean" ||
    typeof data.resetsAt !== "string" ||
    Number.isNaN(Date.parse(data.resetsAt))
  ) {
    throw new Error("The server returned invalid property request limit data.");
  }
  return data;
}

function PropertyLookupLimit({ userId }) {
  const sessionId = useSelector((state) => state.login.sessionId);
  const [usage, setUsage] = useState(null);
  const [dailyLimit, setDailyLimit] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const requestVersion = useRef(0);
  const url = `${WHOLSTER_REST_BASE_URL}/admin/user/${encodeURIComponent(
    userId
  )}/propertyLookupLimit`;

  useEffect(() => {
    const version = ++requestVersion.current;
    setLoading(true);
    setSaving(false);
    setUsage(null);
    setDailyLimit("");
    setMessage(null);

    async function load() {
      try {
        const data = await loadLimit(url, sessionId);
        if (version !== requestVersion.current) return;
        setUsage(data);
        setDailyLimit(String(data.dailyLimit));
      } catch (error) {
        if (version === requestVersion.current) {
          setMessage({
            severity: "error",
            text: error.message || "Unable to load the property request limit.",
          });
        }
      } finally {
        if (version === requestVersion.current) setLoading(false);
      }
    }
    load();
    return () => {
      requestVersion.current += 1;
    };
  }, [url, sessionId, refresh]);

  const saveLimit = async (event) => {
    event.preventDefault();
    if (loading || saving || !usage) return;
    const value = Number(dailyLimit);
    if (
      !/^\d+$/.test(dailyLimit) ||
      !Number.isSafeInteger(value) ||
      value < 0
    ) {
      setMessage({
        severity: "error",
        text: "Enter a non-negative whole number for the daily limit.",
      });
      return;
    }

    const version = requestVersion.current;
    setSaving(true);
    setMessage(null);
    let saved = false;
    try {
      await authenticatedJsonRequest(url, sessionId, {
        method: "PUT",
        body: JSON.stringify({ dailyLimit: value }),
      });
      saved = true;
      if (version !== requestVersion.current) return;
      const data = await loadLimit(url, sessionId);
      if (version !== requestVersion.current) return;
      setUsage(data);
      setDailyLimit(String(data.dailyLimit));
      setMessage({
        severity: "success",
        text: "Daily property request limit saved.",
      });
    } catch (error) {
      if (version === requestVersion.current) {
        if (saved) setUsage(null);
        setMessage({
          severity: "error",
          text: saved
            ? `Limit saved, but usage could not be refreshed: ${error.message}`
            : error.message || "Unable to save the property request limit.",
        });
      }
    } finally {
      if (version === requestVersion.current) setSaving(false);
    }
  };

  return (
    <Card sx={{ boxShadow: "none", height: "100%" }}>
      <MDBox p={2}>
        <MDTypography variant="h6" fontWeight="medium">
          Daily property request limit
        </MDTypography>
      </MDBox>
      <MDBox pt={1} pb={2} px={2}>
        {loading ? (
          <MDBox display="flex" justifyContent="center" py={3}>
            <CircularProgress
              size={28}
              color="info"
              aria-label="Loading property request limit"
            />
          </MDBox>
        ) : (
          <>
            {usage && (
              <MDBox mb={2}>
                <MDTypography display="block" variant="button" color="text">
                  Current daily limit: {usage.dailyLimit}
                </MDTypography>
                <MDTypography display="block" variant="button" color="text">
                  Used today: {usage.usedToday}
                </MDTypography>
                <MDTypography display="block" variant="button" color="text">
                  Remaining today: {usage.remaining}
                </MDTypography>
                <MDTypography
                  display="block"
                  variant="button"
                  color={usage.exceeded ? "error" : "text"}
                >
                  {usage.exceeded
                    ? "Daily limit exceeded"
                    : "Daily limit not exceeded"}
                </MDTypography>
                <MDTypography display="block" variant="caption" color="text">
                  Resets at: {new Date(usage.resetsAt).toLocaleString()} (local
                  time)
                </MDTypography>
              </MDBox>
            )}
            <MDBox component="form" onSubmit={saveLimit}>
              <MDInput
                label="Daily property request limit"
                value={dailyLimit}
                onChange={(event) => setDailyLimit(event.target.value)}
                inputProps={{
                  inputMode: "numeric",
                  disabled: !usage || saving,
                }}
                disabled={!usage || saving}
                fullWidth
              />
              <MDButton
                type="submit"
                color="info"
                fullWidth
                sx={{ mt: 2 }}
                disabled={!usage || saving}
              >
                {saving ? "Saving..." : "Save daily limit"}
              </MDButton>
            </MDBox>
            <MDButton
              variant="outlined"
              color="info"
              fullWidth
              sx={{ mt: 1 }}
              disabled={saving}
              onClick={() => setRefresh((current) => current + 1)}
            >
              Refresh usage
            </MDButton>
          </>
        )}
        {message && (
          <Alert severity={message.severity} sx={{ mt: 2 }}>
            {message.text}
          </Alert>
        )}
      </MDBox>
    </Card>
  );
}

PropertyLookupLimit.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default PropertyLookupLimit;
